import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';

interface LowStockIndicatorProps {
  quantity: number;
  minQuantity: number;
  unit?: string;
  showIcon?: boolean;
  inline?: boolean;
}

export const LowStockIndicator: React.FC<LowStockIndicatorProps> = ({
  quantity,
  minQuantity,
  unit = 'Stk.',
  showIcon = true,
  inline = false,
}) => {
  const isLowStock = quantity <= minQuantity;
  const isOutOfStock = quantity === 0;
  const percentageOfMin = minQuantity > 0 ? (quantity / minQuantity) * 100 : 100;

  const getColor = () => {
    if (isOutOfStock) return 'error.main';
    if (isLowStock) return 'warning.main';
    return 'success.main';
  };

  const getBgColor = () => {
    if (isOutOfStock) return 'error.light';
    if (isLowStock) return 'warning.light';
    return 'transparent';
  };

  const getTooltipText = () => {
    if (isOutOfStock) {
      return `Ausverkauft! Mindestbestand: ${minQuantity} ${unit}`;
    }
    if (isLowStock) {
      return `Niedriger Bestand! Aktuell: ${quantity} ${unit}, Minimum: ${minQuantity} ${unit}`;
    }
    return `Bestand OK. Minimum: ${minQuantity} ${unit}`;
  };

  if (inline) {
    return (
      <Tooltip title={getTooltipText()} arrow>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: getBgColor(),
            color: getColor(),
            fontWeight: 600,
          }}
        >
          {showIcon && isLowStock && <WarningIcon fontSize="small" />}
          <Typography variant="body2" component="span" sx={{ fontWeight: 'inherit' }}>
            {quantity} {unit}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={getTooltipText()} arrow>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            {showIcon && isLowStock && (
              <WarningIcon fontSize="small" sx={{ color: getColor() }} />
            )}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: getColor(),
              }}
            >
              {quantity} {unit}
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary">
            Min: {minQuantity} {unit}
          </Typography>
        </Box>
      </Box>
    </Tooltip>
  );
};

// Utility function to check if item has low stock
export const hasLowStock = (quantity: number, minQuantity: number): boolean => {
  return quantity <= minQuantity;
};

// Utility function to get items with low stock from a list
export const filterLowStockItems = <T extends { quantity: number; min_quantity: number }>(
  items: T[]
): T[] => {
  return items.filter(item => hasLowStock(item.quantity, item.min_quantity));
};
